import {
  Directive,
  Input,
  TemplateRef,
  ViewContainerRef,
  ElementRef,
  Optional,
  OnInit,
  OnDestroy,
  EmbeddedViewRef,
  ChangeDetectorRef,
  effect,
  signal,
  EffectRef,
  inject,
  Injector,
  runInInjectionContext,
} from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { TranslocoEvents } from '@jsverse/transloco';
import { Subscription } from 'rxjs';

interface TranslateContext {
  $implicit: (key: string, params?: Record<string, any>) => string;
  translate: (key: string, params?: Record<string, any>) => string;
  params?: Record<string, any>;
}

@Directive({
  selector: '[translate]',
  standalone: true,
})
export class TranslateDirective implements OnInit, OnDestroy {
  private transloco = inject(TranslocoService);
  private cdr = inject(ChangeDetectorRef);
  private injector = inject(Injector); // For runInInjectionContext
  
  private view?: EmbeddedViewRef<TranslateContext>;
  private subscription = new Subscription();
  
  @Input() translate?: string;
  
  private paramsInputSignal = signal<Record<string, any> | (() => Record<string, any>) | undefined>(undefined);
  private resolvedParamsSignal = signal<Record<string, any> | undefined>(undefined);
  
  private structuralEffectRef?: EffectRef;
  private attributeEffectRef?: EffectRef;
  
  @Input('translateParams') set translateParams(val: Record<string, any> | (() => Record<string, any>) | undefined) {
    this.paramsInputSignal.set(val);
  }
  
  @Input() translateScope?: string;

  constructor(
    @Optional() private tpl?: TemplateRef<TranslateContext>,
    @Optional() private vcr?: ViewContainerRef,
    @Optional() private el?: ElementRef<HTMLElement>
  ) {
    // Shared effect in constructor (injection context)
    effect(() => {
      const paramsInput = this.paramsInputSignal();
      let resolved: Record<string, any> | undefined;
      
      if (typeof paramsInput === 'function') {
        resolved = (paramsInput as () => Record<string, any>)();
      } else {
        resolved = paramsInput;
      }
      
      this.resolvedParamsSignal.set(resolved);
    });
  }

  ngOnInit(): void {
    if (this.tpl && this.vcr) {
      // --- Structural behavior ---
      const ctx: TranslateContext = {
        $implicit: (key: string, params?: Record<string, any>) =>
          this.transloco.translate(key, this.mergeParams(params), this.translateScope),
        translate: (key: string, params?: Record<string, any>) =>
          this.transloco.translate(key, this.mergeParams(params), this.translateScope),
      };
      this.view = this.vcr.createEmbeddedView(this.tpl, ctx);

      // Wrap effect creation in runInInjectionContext
      runInInjectionContext(this.injector, () => {
        this.structuralEffectRef = effect(() => {
          const resolvedParams = this.resolvedParamsSignal();
          ctx.params = resolvedParams;
          this.view?.detectChanges();
        });
      });

      // Language changes
      this.subscription.add(
        this.transloco.langChanges$.subscribe(() => {
          this.view?.detectChanges();
        })
      );
    } else if (this.el && this.translate) {
      // --- Attribute behavior ---
      const update = () => {
        const resolvedParams = this.resolvedParamsSignal();
        const translated = this.transloco.translate(
          this.translate!,
          resolvedParams,
          this.translateScope
        );
        this.el!.nativeElement.textContent = translated;
        this.cdr.markForCheck();
      };
      
      // Sync initial update
      update();
      
      // Wrap effect creation in runInInjectionContext
      runInInjectionContext(this.injector, () => {
        this.attributeEffectRef = effect(() => {
          update();
        });
      });
      
      // Lang change
      this.subscription.add(this.transloco.langChanges$.subscribe(update));
      
      // Async load
      this.subscription.add(
        this.transloco.events$.subscribe((event: TranslocoEvents) => {
          if (event.type === 'translationLoadSuccess') {
            update();
          }
        })
      );
    }
  }
  
  private mergeParams(overrideParams?: Record<string, any>): Record<string, any> | undefined {
    const resolvedBase = this.resolvedParamsSignal();
    return { ...resolvedBase, ...overrideParams };
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.structuralEffectRef?.destroy();
    this.attributeEffectRef?.destroy();
  }
}